import React, { useState, useEffect } from 'react';
import { 
  Building2, Car, Receipt, AlertCircle, CheckCircle, 
  RefreshCw, HelpCircle, DollarSign, Calendar, Bell,
  Info, ExternalLink, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';
import { taxConfigApi, taxRatesApi } from '../services/api';

const STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const ENTITY_TYPES = [
  { value: 'SoleProprietor', label: 'Sole Proprietor', description: 'Most common for single-owner businesses' },
  { value: 'LLC_Single', label: 'Single-Member LLC', description: 'LLC with one owner' },
  { value: 'LLC_Multi', label: 'Multi-Member LLC', description: 'LLC with multiple owners' },
  { value: 'SCorp', label: 'S Corporation', description: 'Pass-through taxation' },
  { value: 'CCorp', label: 'C Corporation', description: 'Separate tax entity' },
];

const VEHICLE_METHODS = [
  { value: 'StandardMileage', label: 'Standard Mileage Rate', description: '70¢/mile (2025)' },
  { value: 'ActualExpense', label: 'Actual Expenses', description: 'Track all vehicle costs' },
];

// State Department of Revenue links (for tax info)
const STATE_TAX_LINKS = {
  'AL': 'https://www.revenue.alabama.gov/',
  'AK': 'https://www.tax.alaska.gov/',
  'AZ': 'https://azdor.gov/',
  'AR': 'https://www.dfa.arkansas.gov/',
  'CA': 'https://www.ftb.ca.gov/',
  'CO': 'https://tax.colorado.gov/',
  'CT': 'https://portal.ct.gov/DRS',
  'DE': 'https://revenue.delaware.gov/',
  'FL': 'https://floridarevenue.com/',
  'GA': 'https://dor.georgia.gov/',
  'HI': 'https://tax.hawaii.gov/',
  'ID': 'https://tax.idaho.gov/',
  'IL': 'https://www2.illinois.gov/rev',
  'IN': 'https://www.in.gov/dor/',
  'IA': 'https://tax.iowa.gov/',
  'KS': 'https://www.ksrevenue.gov/',
  'KY': 'https://revenue.ky.gov/',
  'LA': 'https://revenue.louisiana.gov/',
  'ME': 'https://www.maine.gov/revenue/',
  'MD': 'https://www.marylandtaxes.gov/',
  'MA': 'https://www.mass.gov/orgs/massachusetts-department-of-revenue',
  'MI': 'https://www.michigan.gov/treasury',
  'MN': 'https://www.revenue.state.mn.us/',
  'MS': 'https://www.dor.ms.gov/',
  'MO': 'https://dor.mo.gov/',
  'MT': 'https://mtrevenue.gov/',
  'NE': 'https://revenue.nebraska.gov/',
  'NV': 'https://tax.nv.gov/',
  'NH': 'https://www.revenue.nh.gov/',
  'NJ': 'https://www.nj.gov/treasury/taxation/',
  'NM': 'https://www.tax.newmexico.gov/',
  'NY': 'https://www.tax.ny.gov/',
  'NC': 'https://www.ncdor.gov/',
  'ND': 'https://www.tax.nd.gov/',
  'OH': 'https://tax.ohio.gov/',
  'OK': 'https://oklahoma.gov/tax.html',
  'OR': 'https://www.oregon.gov/dor',
  'PA': 'https://www.revenue.pa.gov/',
  'RI': 'https://tax.ri.gov/',
  'SC': 'https://dor.sc.gov/',
  'SD': 'https://dor.sd.gov/',
  'TN': 'https://www.tn.gov/revenue.html',
  'TX': 'https://comptroller.texas.gov/',
  'UT': 'https://tax.utah.gov/',
  'VT': 'https://tax.vermont.gov/',
  'VA': 'https://www.tax.virginia.gov/',
  'WA': 'https://dor.wa.gov/',
  'WV': 'https://tax.wv.gov/',
  'WI': 'https://www.revenue.wi.gov/',
  'WY': 'https://revenue.wyo.gov/',
};

// ============================================
// TOOLTIP COMPONENT - Mobile Friendly
// ============================================
const Tooltip = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };
  
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="ml-1.5 p-1 text-gray-400 hover:text-blue-600 focus:outline-none rounded-full hover:bg-blue-50 active:bg-blue-100"
        aria-label="More information"
      >
        <HelpCircle size={16} />
      </button>
      
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
          onClick={handleClose}
        >
          <div 
            className="bg-gray-900 text-white text-sm rounded-xl shadow-2xl max-w-sm w-full p-4 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <p className="font-semibold text-base mb-2 text-blue-300">{title}</p>
            )}
            <div className="text-gray-200 space-y-2">{children}</div>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================
// COLLAPSIBLE LEARN MORE SECTION
// ============================================
const LearnMore = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-blue-800">
          <Info size={16} className="text-blue-600" />
          {title}
        </span>
        {isOpen ? (
          <ChevronUp size={18} className="text-blue-600" />
        ) : (
          <ChevronDown size={18} className="text-blue-600" />
        )}
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 text-sm text-blue-900 border-t border-blue-200 pt-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================
// TAX DISCLAIMER - Remembers dismissal
// ============================================
const TaxDisclaimer = () => {
  const STORAGE_KEY = 'suddyflow_tax_disclaimer_dismissed';
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsDismissed(true);
  };

  // Collapsed view (after dismissal)
  if (isDismissed) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-amber-100 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-amber-800">
            <AlertTriangle size={16} className="text-amber-600" />
            Tax Disclaimer
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-amber-600" />
          ) : (
            <ChevronDown size={18} className="text-amber-600" />
          )}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-3 text-sm text-amber-800 border-t border-amber-200 pt-3">
            <p>
              SuddyFlow provides tools to help organize your business finances, but <strong>we are not tax advisors</strong>. 
              Tax laws vary by location and change frequently. Always consult a qualified tax professional (CPA or tax attorney) 
              for advice specific to your situation. The information here is for general guidance only.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Expanded view (first time)
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-amber-900">Important Tax Disclaimer</p>
          <p className="text-sm text-amber-800 mt-1">
            SuddyFlow provides tools to help organize your business finances, but <strong>we are not tax advisors</strong>. 
            Tax laws vary by location and change frequently. Always consult a qualified tax professional (CPA or tax attorney) 
            for advice specific to your situation. The information here is for general guidance only.
          </p>
          <button
            onClick={handleDismiss}
            className="mt-3 px-4 py-1.5 bg-amber-200 hover:bg-amber-300 text-amber-900 text-sm font-medium rounded-lg transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const TaxSettingsTab = ({ showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [federalRates, setFederalRates] = useState(null);
  
  const [config, setConfig] = useState({
    state: '',
    county: '',
    stateIncomeTaxRate: 0,
    entityType: 'SoleProprietor',
    vehicleExpenseMethod: 'StandardMileage',
    vehicleBusinessUsePercent: 100,
    collectsSalesTax: false,
    salesTaxRate: 0,
    salesTaxId: '',
    salesTaxFilingFrequency: 'Quarterly',
    enableQuarterlyTaxReminders: true,
    enableSalesTaxReminders: true,
    reminderDaysBefore: 14,
    isSetupComplete: false,
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [taxAutoEnabled, setTaxAutoEnabled] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const configResponse = await taxConfigApi.getConfiguration();
      if (configResponse.data && configResponse.data.state) {
        setConfig(configResponse.data);
        
        if (configResponse.data.state) {
          loadCounties(configResponse.data.state);
        }
      }

      try {
        const federalResponse = await taxRatesApi.getFederalRates();
        setFederalRates(federalResponse.data);
      } catch (err) {
        console.error('Error loading federal rates:', err);
      }

    } catch (error) {
      console.error('Error loading tax config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCounties = async (stateCode) => {
    if (!stateCode) {
      setCounties([]);
      return;
    }
    
    try {
      setLoadingCounties(true);
      const response = await taxRatesApi.getCounties(stateCode);
      setCounties(response.data || []);
    } catch (error) {
      console.error('Error loading counties:', error);
      setCounties([]);
    } finally {
      setLoadingCounties(false);
    }
  };

  const handleStateChange = async (stateCode) => {
    setConfig(prev => ({
      ...prev,
      state: stateCode,
      county: '',
      salesTaxRate: 0,
    }));
    setHasChanges(true);
    setTaxAutoEnabled(false);
    
    loadCounties(stateCode);
    
    if (stateCode) {
      try {
        const response = await taxRatesApi.getStateIncomeTax(stateCode);
        if (response.data) {
          setConfig(prev => ({
            ...prev,
            stateIncomeTaxRate: response.data.rate || 0,
          }));
        }
      } catch (error) {
        console.error('Error fetching state income tax:', error);
      }
    }
  };

  const handleCountyChange = async (countyName) => {
    setConfig(prev => ({ ...prev, county: countyName }));
    setHasChanges(true);
    setTaxAutoEnabled(false);
    
    if (config.state && countyName) {
      try {
        const response = await taxRatesApi.lookup(config.state, countyName);
        if (response.data && response.data.ratePercent > 0) {
          setConfig(prev => ({
            ...prev,
            salesTaxRate: response.data.ratePercent || 0,
            collectsSalesTax: true,
          }));
          setTaxAutoEnabled(true);
        } else {
          setConfig(prev => ({
            ...prev,
            salesTaxRate: response.data?.ratePercent || 0,
          }));
        }
      } catch (error) {
        console.error('Error looking up tax rate:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await taxConfigApi.saveConfiguration({
        ...config,
        isSetupComplete: true,
      });
      
      const reloadResponse = await taxConfigApi.getConfiguration();
      if (reloadResponse.data) {
        setConfig(reloadResponse.data);
      }
      
      setHasChanges(false);
      setTaxAutoEnabled(false);
      showToast('Tax settings saved successfully!');
    } catch (error) {
      console.error('Error saving tax config:', error);
      showToast('Failed to save tax settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    
    if (field === 'collectsSalesTax') {
      setTaxAutoEnabled(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  const selectedState = STATES.find(s => s.code === config.state);

  return (
    <div className="space-y-6">
      {/* ============================================ */}
      {/* DISCLAIMER BANNER - Collapsible */}
      {/* ============================================ */}
      <TaxDisclaimer />

      {/* Setup Status */}
      {!config.isSetupComplete && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Complete Your Tax Setup</p>
              <p className="text-sm text-blue-700 mt-1">
                Fill in the settings below to enable accurate reporting and tax reminders.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Federal Rates Info */}
      {federalRates && (
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">{federalRates.taxYear} Federal Tax Rates</h3>
            <Tooltip title="What are these?">
              <p>These are IRS rates that apply to all self-employed individuals, regardless of state.</p>
              <p className="mt-2"><strong>Self-Employment Tax:</strong> Covers Social Security + Medicare. You pay this on your business profit.</p>
            </Tooltip>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Self-Employment Tax</p>
              <p className="font-semibold text-gray-900">{federalRates.selfEmploymentRate}%</p>
            </div>
            <div>
              <p className="text-gray-500">SS Wage Base</p>
              <p className="font-semibold text-gray-900">${federalRates.socialSecurityWageBase.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-500">Medicare Surtax</p>
              <p className="font-semibold text-gray-900">{federalRates.medicareAdditionalRate}% (over $200k)</p>
            </div>
            <div>
              <p className="text-gray-500">Mileage Rate</p>
              <p className="font-semibold text-gray-900">{federalRates.standardMileageRate}¢/mile</p>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* BUSINESS LOCATION & ENTITY */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Building2 className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Business Location & Entity
              <Tooltip title="Why does this matter?">
                <p>Your state and business type affect how you file taxes and what forms you use.</p>
                <p className="mt-2">For example, Sole Proprietors use Schedule C, while S-Corps have additional payroll requirements.</p>
              </Tooltip>
            </h3>
            <p className="text-sm text-gray-500">Where is your business based?</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label">State *</label>
            <select
              value={config.state}
              onChange={(e) => handleStateChange(e.target.value)}
              className="input"
            >
              <option value="">Select your state</option>
              {STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">County</label>
            {counties.length > 0 ? (
              <select
                value={config.county}
                onChange={(e) => handleCountyChange(e.target.value)}
                className="input"
                disabled={loadingCounties}
              >
                <option value="">Select your county</option>
                {counties.map((county) => (
                  <option key={county.county} value={county.county}>
                    {county.county} ({county.ratePercent}% sales tax)
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={config.county}
                onChange={(e) => updateConfig('county', e.target.value)}
                className="input"
                placeholder="Enter your county"
              />
            )}
            <p className="text-xs text-gray-500 mt-1">
              {counties.length > 0 
                ? 'Select to auto-fill sales tax rate' 
                : 'County rates not available for this state'}
            </p>
          </div>

          <div>
            <label className="label">
              Business Entity Type
              <Tooltip title="Which should I choose?">
                <p><strong>Sole Proprietor:</strong> Simplest option. You ARE the business. Most new businesses start here.</p>
                <p className="mt-2"><strong>Single-Member LLC:</strong> Provides liability protection but taxed the same as Sole Proprietor.</p>
                <p className="mt-2"><strong>S-Corp:</strong> Can save on self-employment tax but requires paying yourself a salary. Usually makes sense at $40k+ profit.</p>
                <p className="mt-2 text-amber-300">Talk to a CPA before choosing S-Corp!</p>
              </Tooltip>
            </label>
            <select
              value={config.entityType}
              onChange={(e) => updateConfig('entityType', e.target.value)}
              className="input"
            >
              {ENTITY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {ENTITY_TYPES.find(t => t.value === config.entityType)?.description}
            </p>
          </div>

          <div>
            <label className="label">
              State Income Tax Rate
              <Tooltip>
                <p>This is the rate YOUR STATE charges on your business income.</p>
                <p className="mt-2">Some states (like FL, TX, WA) have NO state income tax!</p>
              </Tooltip>
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={config.stateIncomeTaxRate}
                onChange={(e) => updateConfig('stateIncomeTaxRate', parseFloat(e.target.value) || 0)}
                className="input pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
            </div>
            {selectedState && config.stateIncomeTaxRate === 0 && (
              <p className="text-xs text-green-600 mt-1">
                ✓ {selectedState.name} has no state income tax
              </p>
            )}
          </div>
        </div>

        {/* Educational content for entity types */}
        <div className="mt-4">
          <LearnMore title="Not sure about entity types? Learn the basics">
            <div className="space-y-3">
              <p>
                <strong>Most service businesses start as Sole Proprietors</strong> because it's the simplest. 
                You don't need to file separate business tax returns - everything goes on your personal return (Schedule C).
              </p>
              <p>
                <strong>An LLC (Limited Liability Company)</strong> adds legal protection - if someone sues your business, 
                your personal assets (house, car) are generally protected. Many recommend forming an LLC once your 
                business is established.
              </p>
              <p>
                <strong>S-Corp election</strong> can save you money on self-employment tax, but it requires 
                running payroll and paying yourself a "reasonable salary." This usually makes sense when 
                your profit exceeds $40,000-$50,000/year.
              </p>
              <p className="text-amber-700 bg-amber-50 p-2 rounded">
                💡 <strong>Tip:</strong> Start simple. You can always change your entity type later as your business grows.
              </p>
            </div>
          </LearnMore>
        </div>
      </div>

      {/* ============================================ */}
      {/* VEHICLE EXPENSES */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <Car className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Vehicle Expense Tracking
              <Tooltip title="Why track vehicle expenses?">
                <p>Driving to job sites is a business expense! You can deduct it from your taxes.</p>
                <p className="mt-2">There are two ways to calculate this deduction - choose the one that works best for you.</p>
              </Tooltip>
            </h3>
            <p className="text-sm text-gray-500">How do you track vehicle deductions?</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label">
              Tracking Method
              <Tooltip title="Which method should I use?">
                <p><strong>Standard Mileage (Easier):</strong> Just track your miles. Multiply by IRS rate (70¢ in 2025). No receipts needed for vehicle expenses.</p>
                <p className="mt-2"><strong>Actual Expenses (More work):</strong> Track every expense - gas, oil changes, repairs, insurance, registration. Can yield higher deduction but requires more record-keeping.</p>
                <p className="mt-2 text-green-300">Most small service businesses use Standard Mileage for simplicity.</p>
              </Tooltip>
            </label>
            <select
              value={config.vehicleExpenseMethod}
              onChange={(e) => updateConfig('vehicleExpenseMethod', e.target.value)}
              className="input"
            >
              {VEHICLE_METHODS.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label} - {method.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">
              Business Use Percentage
              <Tooltip>
                <p>What % of your driving is for business vs personal?</p>
                <p className="mt-2">If your truck is ONLY used for work, set this to 100%.</p>
                <p className="mt-2">If you also use it for groceries, personal trips, etc., estimate the business portion.</p>
              </Tooltip>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={config.vehicleBusinessUsePercent}
                onChange={(e) => updateConfig('vehicleBusinessUsePercent', parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-lg font-semibold w-16 text-right">
                {config.vehicleBusinessUsePercent}%
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>💡 Keep a mileage log!</strong> The IRS requires documentation of business vs. personal miles. 
            Use an app like MileIQ or just a simple spreadsheet. Record the date, destination, purpose, and miles for each business trip.
          </p>
        </div>
      </div>

      {/* ============================================ */}
      {/* SALES TAX */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Receipt className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Sales Tax Collection
              <Tooltip title="What is sales tax?">
                <p>Sales tax is collected FROM your customers and paid TO your state. You're basically a middleman - it's not your money.</p>
                <p className="mt-2">Whether you need to collect it depends on your state and what services you provide.</p>
              </Tooltip>
            </h3>
            <p className="text-sm text-gray-500">Do you charge sales tax on your services?</p>
          </div>
        </div>

        {/* Auto-enabled notification */}
        {taxAutoEnabled && config.collectsSalesTax && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  Tax collection enabled automatically
                </p>
                <p className="text-xs text-green-700 mt-0.5">
                  Based on your county selection ({config.county} - {config.salesTaxRate}%). 
                  Uncheck below if you don't collect sales tax.
                </p>
              </div>
            </div>
          </div>
        )}

        <label className="flex items-center gap-3 p-4 rounded-lg border cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={config.collectsSalesTax}
            onChange={(e) => updateConfig('collectsSalesTax', e.target.checked)}
            className="w-5 h-5 text-primary-600 rounded"
          />
          <div>
            <p className="font-medium text-gray-900">I collect sales tax from customers</p>
            <p className="text-sm text-gray-500">
              Enable this if you charge sales tax on invoices
            </p>
          </div>
        </label>

        {config.collectsSalesTax && (
          <div className="grid md:grid-cols-3 gap-4 pl-4 border-l-2 border-purple-200">
            <div>
              <label className="label">Sales Tax Rate (%)</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={config.salesTaxRate}
                  onChange={(e) => updateConfig('salesTaxRate', parseFloat(e.target.value) || 0)}
                  className="input pr-8"
                  placeholder="e.g., 7.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
              </div>
              {config.salesTaxRate > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Rate set to {config.salesTaxRate}%
                </p>
              )}
            </div>

            <div>
              <label className="label">
                Sales Tax ID
                <Tooltip>
                  <p>This is the registration number your state gave you when you registered to collect sales tax.</p>
                  <p className="mt-2">In NC, this is your NC Sales and Use Tax Account ID.</p>
                </Tooltip>
              </label>
              <input
                type="text"
                value={config.salesTaxId || ''}
                onChange={(e) => updateConfig('salesTaxId', e.target.value)}
                className="input"
                placeholder="Your state tax ID"
              />
            </div>

            <div>
              <label className="label">
                Filing Frequency
                <Tooltip>
                  <p>How often you must file and pay your collected sales tax to the state.</p>
                  <p className="mt-2">Your state assigns this based on how much tax you collect. Most small businesses file quarterly.</p>
                </Tooltip>
              </label>
              <select
                value={config.salesTaxFilingFrequency}
                onChange={(e) => updateConfig('salesTaxFilingFrequency', e.target.value)}
                className="input"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
          </div>
        )}

        {/* Educational content for sales tax */}
        <div className="mt-4">
          <LearnMore title="Do I need to collect sales tax? Learn more">
            <div className="space-y-3">
              <p>
                <strong>It depends on your state and service type.</strong> Sales tax rules vary widely. 
                Some states tax services, others don't. Some tax specific services but not others.
              </p>
              
              <div className="bg-white p-3 rounded border border-blue-200">
                <p className="font-medium text-blue-900 mb-2">General Guidelines for Service Businesses:</p>
                <ul className="text-sm space-y-1">
                  <li>• Some states tax most services (like cleaning, repairs, landscaping)</li>
                  <li>• Some states only tax specific services</li>
                  <li>• Some states don't tax services at all</li>
                  <li>• Local jurisdictions may add additional taxes</li>
                </ul>
                <p className="text-xs text-blue-700 mt-2">
                  Check your state's department of revenue website or consult a tax professional to determine your requirements.
                </p>
              </div>
              
              <p>
                <strong>If you need to collect sales tax:</strong>
              </p>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Register with your state's department of revenue</li>
                <li>Get a sales tax permit/account number</li>
                <li>Add sales tax to your invoices</li>
                <li>File returns and pay collected tax on schedule</li>
              </ol>
              
              <p className="text-amber-700 bg-amber-50 p-2 rounded">
                💡 <strong>Tip:</strong> Keep the sales tax you collect in a separate account! It's not your money - you're holding it for the state.
              </p>
            </div>
          </LearnMore>
        </div>
      </div>

      {/* ============================================ */}
      {/* TAX REMINDERS */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Bell className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Tax Reminders
              <Tooltip title="What are quarterly taxes?">
                <p>Unlike W-2 employees, self-employed people must pay taxes throughout the year in 4 installments.</p>
                <p className="mt-2">Due dates: April 15, June 15, September 15, January 15</p>
                <p className="mt-2">Miss these and you may owe penalties!</p>
              </Tooltip>
            </h3>
            <p className="text-sm text-gray-500">Get notified before tax deadlines</p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.enableQuarterlyTaxReminders}
              onChange={(e) => updateConfig('enableQuarterlyTaxReminders', e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">Quarterly estimated tax reminders</p>
              <p className="text-xs text-gray-500">Federal estimated payments (April 15, June 15, Sept 15, Jan 15)</p>
            </div>
          </label>

          {config.collectsSalesTax && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.enableSalesTaxReminders}
                onChange={(e) => updateConfig('enableSalesTaxReminders', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Sales tax filing reminders</p>
                <p className="text-xs text-gray-500">Based on your {config.salesTaxFilingFrequency?.toLowerCase()} filing schedule</p>
              </div>
            </label>
          )}

          <div className="pt-3 border-t border-gray-200">
            <label className="text-sm text-gray-600">Remind me this many days before deadline:</label>
            <select
              value={config.reminderDaysBefore}
              onChange={(e) => updateConfig('reminderDaysBefore', parseInt(e.target.value))}
              className="input mt-2 w-40"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={21}>21 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
        </div>

        {/* Educational content for quarterly taxes */}
        <div className="mt-4">
          <LearnMore title="New to quarterly taxes? Here's what you need to know">
            <div className="space-y-3">
              <p>
                <strong>As a self-employed person, you're responsible for paying your own taxes.</strong> 
                Unlike W-2 employees who have taxes withheld from each paycheck, you pay in quarterly installments.
              </p>
              
              <div className="bg-white p-3 rounded border border-blue-200">
                <p className="font-medium text-blue-900 mb-2">2025 Quarterly Tax Due Dates:</p>
                <ul className="text-sm space-y-1">
                  <li>• <strong>Q1:</strong> April 15, 2025 (for Jan-Mar income)</li>
                  <li>• <strong>Q2:</strong> June 16, 2025 (for Apr-May income)</li>
                  <li>• <strong>Q3:</strong> September 15, 2025 (for Jun-Aug income)</li>
                  <li>• <strong>Q4:</strong> January 15, 2026 (for Sep-Dec income)</li>
                </ul>
              </div>
              
              <p>
                <strong>How much should you pay?</strong> A simple rule of thumb: set aside 25-30% of your profit for taxes. 
                Use IRS Form 1040-ES to calculate your estimated payments, or ask a tax professional.
              </p>
              
              <p className="text-amber-700 bg-amber-50 p-2 rounded">
                💡 <strong>Tip:</strong> Open a separate savings account just for taxes. Every time you get paid, 
                transfer 25-30% into that account. When quarterly taxes are due, the money is already there!
              </p>
              
              <p className="text-sm text-gray-600">
                <a 
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/estimated-taxes" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Learn more on IRS.gov <ExternalLink size={14} />
                </a>
              </p>
            </div>
          </LearnMore>
        </div>
      </div>

      {/* ============================================ */}
      {/* RESOURCES & HELP */}
      {/* ============================================ */}
      <div className="card bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-3">📚 Helpful Resources</h3>
        <div className="flex flex-col sm:flex-row gap-3 text-sm">
          <a 
            href="https://www.irs.gov/businesses/small-businesses-self-employed" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <ExternalLink size={16} className="text-blue-600 flex-shrink-0" />
            <span>IRS Self-Employed Tax Center</span>
          </a>
          {config.state && STATE_TAX_LINKS[config.state] && (
            <a 
              href={STATE_TAX_LINKS[config.state]} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              <ExternalLink size={16} className="text-blue-600 flex-shrink-0" />
              <span>{selectedState?.name} Department of Revenue</span>
            </a>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          External resources for reference. SuddyFlow is not affiliated with these organizations.
        </p>
      </div>

      {/* ============================================ */}
      {/* SAVE BUTTON */}
      {/* ============================================ */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div>
          {hasChanges && (
            <p className="text-sm text-amber-600">You have unsaved changes</p>
          )}
          {!hasChanges && config.isSetupComplete && (
            <p className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              Settings saved
            </p>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save Tax Settings'}
        </button>
      </div>

      {/* Final Disclaimer */}
      <div className="text-xs text-gray-500 text-center p-4 bg-gray-50 rounded-lg">
        <p>
          The information provided in SuddyFlow is for general informational purposes only and should not be construed as 
          professional tax, legal, or financial advice. Tax laws are complex and subject to change. 
          Always consult with a qualified tax professional regarding your specific situation.
        </p>
      </div>
    </div>
  );
};

export default TaxSettingsTab;